import { validationTypes } from '../../../components/forms/libs/data/ValidationTypes';
import PropTypes from 'prop-types';

/**
 * Removes specified fields from the form data by their IDs and updates the state.
 *
 * @param {object} formData - The current form data object, including initial state and form structure.
 * @param {Array<string>} fieldIds - An array of field IDs to be removed from the form data.
 * @returns {object} - The updated form data object.
 */

export const removeFieldsById = (formData, fieldIds) => {
    // Remove fields from the initial state
    fieldIds.forEach((id) => {
        delete formData.initialState[id];
    });

    // Iterate through sections
    formData.section.forEach((section) => {
        // Handle fields directly under the section
        if (Array.isArray(section.fields)) {
            section.fields = section.fields.filter((field) => !fieldIds.includes(field.id));
        }

        // Handle fields within rowGroups
        if (Array.isArray(section.rowGroup)) {
            section.rowGroup.forEach((group) => {
                if (Array.isArray(group.fields)) {
                    group.fields = group.fields.filter((field) => !fieldIds.includes(field.id));
                }
            });
        }
    });

    return formData;
};


// PropTypes validation for removeFieldsById function
removeFieldsById.propTypes = {
    formData: PropTypes.shape({
        initialState: PropTypes.object.isRequired,
        section: PropTypes.arrayOf(
            PropTypes.shape({
                rowGroup: PropTypes.arrayOf(
                    PropTypes.shape({
                        fields: PropTypes.arrayOf(
                            PropTypes.shape({
                                id: PropTypes.string.isRequired
                            })
                        )
                    })
                )
            })
        )
    }).isRequired,
    fieldIds: PropTypes.arrayOf(PropTypes.string).isRequired
};


export const removeFieldsById1 = (formData, fieldIds) => {
  // Remove fields from initial state
  fieldIds.forEach((id) => {
    delete formData.initialState[id];
  });

  // Iterate through sections
  formData.section = formData.section
    .map((section) => {
      // Handle rowGroups
      if (Array.isArray(section.rowGroup)) {
        section.rowGroup = section.rowGroup
          .map((group) => {
            if (Array.isArray(group.fields)) {
              group.fields = group.fields.filter(
                (field) => !fieldIds.includes(field.id)
              );
            }

            // ❌ Remove empty group
            return group.fields?.length ? group : null;
          })
          .filter(Boolean);
      }

      // ❌ Remove section if no rowGroups left
      return section.rowGroup?.length ? section : null;
    })
    .filter(Boolean);

  return formData;
};
/**
 * Adds new fields to the form data after a specified target field ID and updates the state.
 *
 * @param {object} formData - The current form data object, including initial state and form structure.
 * @param {Array<object>} fieldsToAdd - An array of field objects to be added to the form.
 * @param {string} targetId - The ID of the field after which the new fields should be added.
 * @returns {object} - The updated form data object.
 */

export const addFormFields = (formData, fieldsToAdd, targetId) => {
    let isFieldAdded = false;
    // Iterate through sections to handle both section.fields and section.rowGroup
    formData?.section?.forEach((section) => {
        // Handle fields directly under the section
        if (Array.isArray(section.fields)) {
            const targetFieldIndex = section?.fields?.findIndex((field) => field.id === targetId);

            if (targetFieldIndex !== -1) {
                // Check if the field already exists in the section
                const existingField = fieldsToAdd.some((field) => section.fields.some((sectionField) => sectionField.id === field.id));

                if (!existingField) {
                    // Add new fields after the target field
                    section.fields.splice(targetFieldIndex + 1, 0, ...fieldsToAdd);

                    // Update the initial state with default values for the new fields
                    fieldsToAdd.forEach((field) => {
                        if (field.dataField) {
                            formData.initialState[field.dataField] = field.defaultValue || null;
                        }
                    });
                    isFieldAdded = true;
                } else {
                    console.log('Field(s) already exist in the section.');
                }
            }
        }

        // Handle fields within rowGroups
        if (Array.isArray(section.rowGroup)) {
            section.rowGroup.forEach((group) => {
                if (Array.isArray(group.fields)) {
                    const targetFieldIndex = group.fields.findIndex((field) => field.id === targetId);

                    if (targetFieldIndex !== -1) {
                        // Check if the field already exists in the rowGroup
                        const existingField = fieldsToAdd.some((field) => group.fields.some((groupField) => groupField.id === field.id));

                        if (!existingField) {
                            // Add new fields after the target field
                            group.fields.splice(targetFieldIndex + 1, 0, ...fieldsToAdd);

                            // Update the initial state with default values for the new fields
                            fieldsToAdd.forEach((field) => {
                                if (field.dataField) {
                                    formData.initialState[field.dataField] = field.defaultValue || null;
                                }
                            });
                            isFieldAdded = true;
                        } else {
                            console.log('Field(s) already exist in the rowGroup.');
                        }
                    }
                }
            });
        }
    });

    if (!isFieldAdded) {
        console.error(`Field with ID "${targetId}" not found or already exists.`);
    }

    return formData;
};


// PropTypes validation for addFormFields function
addFormFields.propTypes = {
    formData: PropTypes.shape({
        initialState: PropTypes.object.isRequired,
        section: PropTypes.arrayOf(
            PropTypes.shape({
                rowGroup: PropTypes.arrayOf(
                    PropTypes.shape({
                        fields: PropTypes.arrayOf(
                            PropTypes.shape({
                                id: PropTypes.string.isRequired
                            })
                        )
                    })
                )
            })
        )
    }).isRequired,
    fieldsToAdd: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string.isRequired,
            dataField: PropTypes.string,
            defaultValue: PropTypes.any
        })
    ).isRequired,
    targetId: PropTypes.string.isRequired
};

/**
 * Generic function to update multiple fields in any form data structure
 * - Remove validation with validationTypes.REQUIRE
 * - Add isDisable: true in fieldSetting
 *
 * @param {Object} formData - The form configuration object
 * @param {Array<string>} targetIds - The array of field IDs to modify
 * @returns {Object} Updated formData object with all sections intact
 */
export const removeValidationAndDisableFields = (formData, targetIds) => {
    const updatedFormData = { ...formData };

    updatedFormData.section = updatedFormData.section.map((section) => {
        // Update fields directly under `section`
        if (section.fields) {
            section.fields = section.fields.map((field) => {
                if (targetIds.includes(field.id)) {
                    // Remove validation that has validationTypes.REQUIRE
                    if (field.validation) {
                        field.validation = field.validation.filter(
                            (v) => v.type !== validationTypes.REQUIRE
                        );
                    }

                    // Add isDisable: true to fieldSetting if it exists
                    if (field.fieldSetting) {
                        field.fieldSetting = {
                            ...field.fieldSetting,
                            isDisable: true,
                        };
                    }
                }
                return field;
            });
        }

        // Update fields within rowGroups (if present)
        if (section.rowGroup) {
            section.rowGroup = section.rowGroup.map((group) => {
                group.fields = group.fields.map((field) => {
                    if (targetIds.includes(field.id)) {
                        // Remove validation that has validationTypes.REQUIRE
                        if (field.validation) {
                            field.validation = field.validation.filter(
                                (v) => v.type !== validationTypes.REQUIRE
                            );
                        }

                        // Add isDisable: true to fieldSetting if it exists
                        if (field.fieldSetting) {
                            field.fieldSetting = {
                                ...field.fieldSetting,
                                isDisable: true,
                            };
                        }
                    }
                    return field;
                });
                return group;
            });
        }

        return section;
    });

    return updatedFormData; // Return the entire updated object
};

/**
 * Generic function to update multiple fields in any form data structure
 * - Add validation with validationTypes.REQUIRE
 * - Set isDisable: false in fieldSetting
 *
 * @param {Object} formData - The form configuration object
 * @param {Array<string>} targetIds - The array of field IDs to modify
 * @returns {Object} Updated formData object with all sections intact
 */
export const addValidationAndEnableFields = (formData, targetIds) => {
    const updatedFormData = { ...formData };

    updatedFormData.section = updatedFormData.section.map((section) => {
        // Update fields directly under `section`
        if (section.fields) {
            section.fields = section.fields.map((field) => {
                if (targetIds.includes(field.id)) {
                    // Add validation with validationTypes.REQUIRE if it doesn't already exist
                    if (field.validation) {
                        const isRequirePresent = field.validation.some(
                            (v) => v.type === validationTypes.REQUIRE
                        );
                        if (!isRequirePresent) {
                            field.validation.push({ type: validationTypes.REQUIRE });
                        }
                    } else {
                        field.validation = [{ type: validationTypes.REQUIRE }];
                    }

                    // Set isDisable: false in fieldSetting if it exists
                    if (field.fieldSetting) {
                        field.fieldSetting = {
                            ...field.fieldSetting,
                            isDisable: false,
                        };
                    }
                }
                return field;
            });
        }

        // Update fields within rowGroups (if present)
        if (section.rowGroup) {
            section.rowGroup = section.rowGroup.map((group) => {
                group.fields = group.fields.map((field) => {
                    if (targetIds.includes(field.id)) {
                        // Add validation with validationTypes.REQUIRE if it doesn't already exist
                        if (field.validation) {
                            const isRequirePresent = field.validation.some(
                                (v) => v.type === validationTypes.REQUIRE
                            );
                            if (!isRequirePresent) {
                                field.validation.push({ type: validationTypes.REQUIRE });
                            }
                        } else {
                            field.validation = [{ type: validationTypes.REQUIRE }];
                        }

                        // Set isDisable: false in fieldSetting if it exists
                        if (field.fieldSetting) {
                            field.fieldSetting = {
                                ...field.fieldSetting,
                                isDisable: false,
                            };
                        }
                    }
                    return field;
                });
                return group;
            });
        }

        return section;
    });

    return updatedFormData; // Return the entire updated object
};

